// Marking-tool match engine.
//
// Selection is a lexicographic filter-and-relax cascade rather than a
// weighted score: each stage partitions the surviving candidates into
// preference tiers (exact match first, progressively relaxed after) and the
// first non-empty tier wins. A stage whose every tier is empty is skipped
// entirely, so the pipeline degrades gracefully dimension by dimension and
// — by construction — never empties a non-empty pool. That is what
// guarantees "always assign the closest tool" even for subjects with no
// exactly-matching templates.
//
// Stage order encodes priority: subject > task type > response mode >
// state > year. Within the final survivor set, nuance comparators decide
// (stimulus with/without, compare-variant, marks proximity, specificity),
// ending on a stable id ordering — never alphabetical titles, which the
// corpus has shown picks "Section III (with Stimulus)" for stimulus-less
// questions and "5 Mark Compare Question" for plain 5-markers.

import {
  RESPONSE_MODES,
  bodyVariantOf,
  compareSignalOf,
  countryForStates,
  deriveResponseMode,
  getYearNum,
  isCompareTitle,
  normalizeCompact,
  parseSubjectCode,
  parseTitleMarkRanges,
  responseModeOf,
  responseVariantOf,
  sectionOf,
  stimulusVariantOf,
  successCriteriaSetKey,
  taskTypeAffinity,
  titleMatchesMarks,
} from './toolVocabulary.js';

// ---------------------------------------------------------------------------
// Tool accessors
// ---------------------------------------------------------------------------

// Read matching metadata with top-level fallback: some legacy payloads carry
// states/subjects at the top level rather than under metadata (other UI code
// already reads them that way).
const metaListOf = (tool, field) => {
  const fromMeta = tool?.metadata?.[field];
  if (Array.isArray(fromMeta) && fromMeta.length > 0) return fromMeta;
  const topLevel = tool?.[field];
  return Array.isArray(topLevel) ? topLevel : [];
};

const toolSubjectCodes = (tool) =>
  metaListOf(tool, 'subjects')
    .map((s) => (typeof s === 'string' ? s : s?.subjectCode ?? s?.code ?? ''))
    .filter(Boolean);

const toolStates = (tool) =>
  metaListOf(tool, 'states')
    .map((s) => String(s || '').toUpperCase())
    .filter(Boolean);

const toolYears = (tool) =>
  metaListOf(tool, 'years')
    .map(getYearNum)
    .filter((y) => y != null);

const toolTaskTypes = (tool) => metaListOf(tool, 'taskTypes').filter(Boolean);

const toolResponseModes = (tool) =>
  metaListOf(tool, 'responseTypes').map(responseModeOf).filter(Boolean);

const toolCountry = (tool) => {
  const fromStates = countryForStates(toolStates(tool));
  if (fromStates) return fromStates;
  const parsed = toolSubjectCodes(tool)
    .map(parseSubjectCode)
    .find((p) => p?.country);
  return parsed ? parsed.country : null;
};

// Placeholder entries the UI injects into pools ("Select Rubric") must never
// be recommended.
const isRealTool = (tool) =>
  Boolean(tool && tool.id && tool.id !== 'no_marking_criteria');

const isRubric = (tool) => !tool?.type || tool.type === 'RUBRICS';

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

/**
 * Normalize the raw question/class context into the derived form every
 * stage consumes. Accepts the same shape recommendMarkingTools always took
 * plus the new nuance fields — all optional.
 */
export const deriveMatchContext = (context = {}) => {
  const subjects = (context.subjects || [])
    .map((s) => String(s || '').trim())
    .filter(Boolean);
  const subjectInfos = subjects.map(parseSubjectCode).filter(Boolean);
  const subjectCompacts = new Set(subjectInfos.map((p) => p.compact));

  // Class jurisdiction beats the profile: an AU-VIC-* or NZ-* subject code
  // pins the state regardless of what the teacher's profile says (the same
  // rule the backend adopted for country scoping in JED-1093).
  const codeStates = [
    ...new Set(subjectInfos.map((p) => p.state).filter(Boolean)),
  ];
  const state =
    codeStates.length === 1
      ? codeStates[0]
      : String(context.state || '').toUpperCase() || null;

  const country =
    subjectInfos.map((p) => p.country).find(Boolean) ||
    countryForStates(state ? [state] : []) ||
    null;

  const years = (context.years || []).map(getYearNum).filter((y) => y != null);
  subjectInfos.forEach((p) => {
    if (p.year != null && !years.includes(p.year)) years.push(p.year);
  });

  const mode = deriveResponseMode(context.responseType, context.taskType);
  const marks = Number.isInteger(context.marksOutOf)
    ? context.marksOutOf
    : null;

  return {
    subjects,
    subjectInfos,
    subjectCompacts,
    state,
    country,
    years,
    taskType: String(context.taskType || '').trim(),
    responseType: String(context.responseType || '').trim(),
    mode,
    marks,
    hasStimulus: Boolean(context.hasStimulus),
    compareSignal: Boolean(
      context.compareSignal ??
        compareSignalOf(context.taskType, context.questionText)
    ),
    introParagraphs: getYearNum(context.introParagraphs) ?? 0,
    bodyParagraphs: getYearNum(context.bodyParagraphs) ?? 1,
    conclusionParagraphs: getYearNum(context.conclusionParagraphs) ?? 0,
  };
};

// ---------------------------------------------------------------------------
// Cascade
// ---------------------------------------------------------------------------

/**
 * Run tiered stages over the pool. Each stage maps the current survivors to
 * an ordered list of tiers; the first non-empty tier becomes the survivor
 * set. A stage with no non-empty tier is skipped (survivors unchanged), so
 * the cascade can only narrow, never empty.
 */
const runCascade = (pool, stages, trace) => {
  let survivors = pool;
  stages.forEach(({ name, tiers }) => {
    const partitions = tiers(survivors);
    for (let i = 0; i < partitions.length; i += 1) {
      if (partitions[i].length > 0) {
        if (trace) trace.push(`${name}:tier${i + 1}`);
        survivors = partitions[i];
        return;
      }
    }
    if (trace) trace.push(`${name}:skipped`);
  });
  return survivors;
};

const subjectStage = (ctx) => ({
  name: 'subject',
  tiers: (tools) => {
    if (ctx.subjectCompacts.size === 0) return [];
    const exact = [];
    const stateFamily = [];
    const family = [];
    const sameCountry = [];
    const undeclared = [];
    tools.forEach((tool) => {
      const codes = toolSubjectCodes(tool);
      if (codes.length === 0) {
        undeclared.push(tool);
        return;
      }
      const parsed = codes.map(parseSubjectCode).filter(Boolean);
      if (parsed.some((p) => ctx.subjectCompacts.has(p.compact))) {
        exact.push(tool);
        return;
      }
      // Family relaxation: same discipline in the same country. Same-state
      // curricula are closer (AU-VIC-EAL -> VCE English) than cross-state
      // ones (AU-VIC-Economics -> the NSW Economics library), but both beat
      // an unrelated subject.
      const relaxed = parsed.some((toolSub) =>
        ctx.subjectInfos.some(
          (ctxSub) =>
            toolSub.family &&
            toolSub.family === ctxSub.family &&
            toolSub.country === ctxSub.country
        )
      );
      if (relaxed) {
        const sameState = parsed.some((toolSub) =>
          ctx.subjectInfos.some(
            (ctxSub) =>
              toolSub.family === ctxSub.family &&
              (toolSub.universal ||
                ctxSub.universal ||
                !ctxSub.state ||
                !toolSub.state ||
                toolSub.state === ctxSub.state)
          )
        );
        (sameState ? stateFamily : family).push(tool);
        return;
      }
      if (
        ctx.country &&
        (toolCountry(tool) === ctx.country || toolCountry(tool) === null)
      ) {
        sameCountry.push(tool);
      }
    });
    return [exact, stateFamily, family, [...sameCountry, ...undeclared]];
  },
});

const responseModeStage = (ctx) => ({
  name: 'responseMode',
  tiers: (tools) => {
    if (!ctx.mode) return [];
    const exact = [];
    const compatible = [];
    tools.forEach((tool) => {
      const modes = toolResponseModes(tool);
      if (modes.length === 0) {
        compatible.push(tool);
        return;
      }
      if (modes.includes(ctx.mode)) {
        exact.push(tool);
        return;
      }
      // Rubrics/tools authored for full extended responses apply equally to
      // targeted-paragraph work (the NZ library declares only
      // "Extended Response" yet must serve Targeted Paragraphs questions).
      if (
        ctx.mode === RESPONSE_MODES.TARGETED &&
        modes.includes(RESPONSE_MODES.EXTENDED)
      ) {
        compatible.push(tool);
      }
    });
    return [exact, compatible];
  },
});

const taskTypeStage = (ctx) => ({
  name: 'taskType',
  tiers: (tools) => {
    if (!ctx.taskType) return [];
    // Compact comparison so punctuation spacing never splits a match
    // ("Analytical / Explanatory Essay" == "Analytical/Explanatory Essay").
    const wanted = normalizeCompact(ctx.taskType);
    const exact = [];
    const declaredFuzzy = [];
    const titleMatch = [];
    const undeclared = [];
    tools.forEach((tool) => {
      const declared = toolTaskTypes(tool);
      if (declared.length > 0) {
        if (declared.some((t) => normalizeCompact(t) === wanted)) {
          exact.push(tool);
        } else if (
          declared.some((t) => taskTypeAffinity(t, ctx.taskType) >= 0.5)
        ) {
          declaredFuzzy.push(tool);
        }
        return;
      }
      if (taskTypeAffinity(tool?.title, ctx.taskType) >= 0.5) {
        titleMatch.push(tool);
      } else {
        undeclared.push(tool);
      }
    });
    return [exact, declaredFuzzy, titleMatch, undeclared];
  },
});

const stateStage = (ctx) => ({
  name: 'state',
  tiers: (tools) => {
    if (!ctx.state) return [];
    const exact = [];
    const undeclared = [];
    tools.forEach((tool) => {
      const states = toolStates(tool);
      if (states.length === 0) {
        undeclared.push(tool);
        return;
      }
      if (states.includes(ctx.state)) exact.push(tool);
    });
    return [exact, undeclared];
  },
});

const yearStage = (ctx) => ({
  name: 'year',
  tiers: (tools) => {
    if (ctx.years.length === 0) return [];
    const exact = [];
    const undeclared = [];
    const rest = [];
    tools.forEach((tool) => {
      const years = toolYears(tool);
      if (years.length === 0) {
        undeclared.push(tool);
        return;
      }
      if (years.some((y) => ctx.years.includes(y))) exact.push(tool);
      else rest.push(tool);
    });
    const yearDistance = (tool) =>
      Math.min(
        ...toolYears(tool).map((y) =>
          Math.min(...ctx.years.map((cy) => Math.abs(cy - y)))
        )
      );
    const nearest = rest
      .map((tool) => ({ tool, d: yearDistance(tool) }))
      .sort((a, b) => a.d - b.d);
    const nearTier =
      nearest.length > 0
        ? nearest.filter((n) => n.d === nearest[0].d).map((n) => n.tool)
        : [];
    return [exact, undeclared, nearTier];
  },
});

// Task type before response mode: the task type names the GENRE (the NZ
// "Close Reading / Unfamiliar Text" checklist declares Short Answer as its
// response type, yet it is the only right pick for a close-reading task in
// any response mode); the response mode is only a tie-breaker between
// variants of the same genre.
const standardStages = (ctx) => [
  subjectStage(ctx),
  taskTypeStage(ctx),
  responseModeStage(ctx),
  stateStage(ctx),
  yearStage(ctx),
];

// ---------------------------------------------------------------------------
// Final ranking (within the surviving tier)
// ---------------------------------------------------------------------------

const stimulusScore = (tool, ctx) => {
  const variant = stimulusVariantOf(tool?.title);
  const preferred = ctx.hasStimulus ? 'with' : 'without';
  if (variant === null) return 1;
  return variant === preferred ? 2 : 0;
};

const compareScore = (tool, ctx) => {
  const titled = isCompareTitle(tool?.title);
  if (ctx.compareSignal) return titled ? 2 : 1;
  return titled ? 0 : 1;
};

const specificityScore = (tool, ctx) => {
  let score = 0;
  const codes = toolSubjectCodes(tool).map(normalizeCompact);
  if (codes.some((c) => ctx.subjectCompacts.has(c))) score += 2;
  if (ctx.taskType) {
    const wanted = normalizeCompact(ctx.taskType);
    if (toolTaskTypes(tool).some((t) => normalizeCompact(t) === wanted))
      score += 2;
    else if (taskTypeAffinity(tool?.title, ctx.taskType) >= 0.5) score += 1;
  }
  if (ctx.mode && toolResponseModes(tool).includes(ctx.mode)) score += 1;
  if (ctx.state && toolStates(tool).includes(ctx.state)) score += 1;
  if (
    ctx.years.length > 0 &&
    toolYears(tool).some((y) => ctx.years.includes(y))
  ) {
    score += 1;
  }
  return score;
};

// Tighter scoping breaks remaining ties: a single-state, two-year tool beats
// a 51-state blanket. When the context has NO state signal, the preference
// flips for states — broader coverage is the safer prior than an arbitrary
// single state.
const tightnessScore = (tool, ctx) => {
  const stateCount = toolStates(tool).length;
  const spread =
    toolYears(tool).length +
    toolSubjectCodes(tool).length +
    (ctx.state ? stateCount : Math.max(0, 60 - stateCount));
  return -spread;
};

// "Copy of ..." duplicates exist in the library; when everything else ties,
// prefer the original.
const copyPenalty = (tool) =>
  /^copy of\b/i.test(String(tool?.title || '')) ? 1 : 0;

const rankSurvivors = (tools, ctx) =>
  [...tools].sort((a, b) => {
    const stim = stimulusScore(b, ctx) - stimulusScore(a, ctx);
    if (stim !== 0) return stim;
    const cmp = compareScore(b, ctx) - compareScore(a, ctx);
    if (cmp !== 0) return cmp;
    const spec = specificityScore(b, ctx) - specificityScore(a, ctx);
    if (spec !== 0) return spec;
    const copy = copyPenalty(a) - copyPenalty(b);
    if (copy !== 0) return copy;
    const tight = tightnessScore(b, ctx) - tightnessScore(a, ctx);
    if (tight !== 0) return tight;
    return String(a?.id || '').localeCompare(String(b?.id || ''));
  });

// ---------------------------------------------------------------------------
// Per-tool-type selection
// ---------------------------------------------------------------------------

export const selectRubric = (markingCriterias, ctx, trace) => {
  if (ctx.mode === RESPONSE_MODES.SHORT) return null;
  const pool = (markingCriterias || []).filter(isRealTool).filter(isRubric);
  if (pool.length === 0) return null;
  const survivors = runCascade(pool, standardStages(ctx), trace);
  return rankSurvivors(survivors, ctx)[0] || null;
};

export const selectCommentBank = (commentBanks, ctx, trace) => {
  if (ctx.mode === RESPONSE_MODES.SHORT) return null;
  const pool = (commentBanks || []).filter(isRealTool);
  if (pool.length === 0) return null;
  const survivors = runCascade(pool, standardStages(ctx), trace);
  return rankSurvivors(survivors, ctx)[0] || null;
};

// Distance from the requested marks to whatever marks signal a tool carries:
// structured highestMarks, or title marks/ranges ("3–8 Mark Question" is 0
// away inside the range, otherwise the gap to the nearest endpoint). Null
// when the tool carries no marks signal at all.
const marksDistanceOf = (tool, marks) => {
  const distances = [];
  const hm = getYearNum(tool?.highestMarks);
  if (hm != null) distances.push(Math.abs(hm - marks));
  parseTitleMarkRanges(tool?.title).forEach((r) => {
    if (marks < r.min) distances.push(r.min - marks);
    else if (marks > r.max) distances.push(marks - r.max);
    else distances.push(0);
  });
  return distances.length > 0 ? Math.min(...distances) : null;
};

const nearestByMarks = (tools, marks) => {
  const entries = tools
    .map((tool) => ({ tool, d: marksDistanceOf(tool, marks) }))
    .filter((e) => e.d != null)
    .sort((a, b) => a.d - b.d);
  return entries.length > 0
    ? entries.filter((e) => e.d === entries[0].d).map((e) => e.tool)
    : [];
};

const marksStage = (ctx) => ({
  name: 'marks',
  tiers: (tools) => {
    if (ctx.marks == null) return [];
    const exact = tools.filter(
      (t) => getYearNum(t?.highestMarks) === ctx.marks
    );
    const titled = tools.filter((t) => titleMatchesMarks(t?.title, ctx.marks));
    return [exact, titled, nearestByMarks(tools, ctx.marks)];
  },
});

export const selectShortAnswer = (shortAnswers, ctx, trace) => {
  if (ctx.mode !== RESPONSE_MODES.SHORT) return null;
  const pool = (shortAnswers || []).filter(isRealTool);
  if (pool.length === 0) return null;
  const survivors = runCascade(
    pool,
    [...standardStages(ctx), marksStage(ctx)],
    trace
  );
  return rankSurvivors(survivors, ctx)[0] || null;
};

// --- Success criteria -------------------------------------------------------

// Success criteria written for short-answer marking: declare a
// short-answer task/response type, the question's own task type, or carry
// marks in the title ("3 Mark Question"). Everything else in the SC pool is
// essay-section material and the wrong genre for a short answer.
const shortAnswerSuccessCriteriaGenreStage = (ctx) => ({
  name: 'saGenre',
  tiers: (tools) => {
    const wanted = normalizeCompact(ctx.taskType);
    const flavored = tools.filter((tool) => {
      if (parseTitleMarkRanges(tool?.title).length > 0) return true;
      const declared = toolTaskTypes(tool);
      if (
        declared.some(
          (t) =>
            responseModeOf(t) === RESPONSE_MODES.SHORT ||
            (wanted && normalizeCompact(t) === wanted)
        )
      ) {
        return true;
      }
      return toolResponseModes(tool).includes(RESPONSE_MODES.SHORT);
    });
    return [flavored];
  },
});

const shortAnswerSuccessCriteriaMarksStage = (ctx) => ({
  name: 'saMarks',
  tiers: (tools) => {
    if (ctx.marks == null) return [];
    const exactSingle = tools.filter((t) =>
      parseTitleMarkRanges(t?.title).some(
        (r) => r.min === ctx.marks && r.max === ctx.marks
      )
    );
    const inRange = tools.filter((t) => titleMatchesMarks(t?.title, ctx.marks));
    return [exactSingle, inRange, nearestByMarks(tools, ctx.marks)];
  },
});

const neededSections = (ctx) => {
  if (ctx.mode === RESPONSE_MODES.TARGETED) {
    const wanted = [];
    if (ctx.introParagraphs > 0) wanted.push('intro');
    if (ctx.bodyParagraphs > 0) wanted.push('body');
    if (ctx.conclusionParagraphs > 0) wanted.push('conclusion');
    return wanted.length > 0 ? wanted : ['body'];
  }
  return ['intro', 'body', 'conclusion'];
};

const preferredBodyVariant = (ctx) =>
  ctx.mode === RESPONSE_MODES.TARGETED && ctx.bodyParagraphs === 1
    ? 'single'
    : 'multiple';

const preferredResponseVariant = (ctx) =>
  ctx.mode === RESPONSE_MODES.TARGETED ? 'targeted' : 'full';

const sectionVariantScore = (tool, ctx) => {
  let score = stimulusScore(tool, ctx) * 4 + compareScore(tool, ctx);
  const body = bodyVariantOf(tool?.title);
  if (body) score += body === preferredBodyVariant(ctx) ? 4 : -4;
  const responseVariant = responseVariantOf(tool?.title);
  if (responseVariant) {
    score += responseVariant === preferredResponseVariant(ctx) ? 2 : -2;
  }
  return score;
};

/**
 * Pick the success-criteria list. Three corpus shapes are handled:
 *  - AU sectioned sets: bare "Introduction" / "Body Paragraph (Single|
 *    Multiple)" / "Conclusion" titles plus stimulus / "Section III"
 *    variants — one pick per needed section, variant-resolved.
 *  - US genre sets: "{Genre} — {Section}" including extra sections
 *    (Counterclaim, Complexity...) — the whole coherent set, keeping every
 *    distinct section, resolving variants within a section.
 *  - NZ/AU single checklists: unsectioned one-tool sets — the single best.
 */
export const selectSuccessCriteria = (successCriteria, ctx, trace) => {
  const pool = (successCriteria || []).filter(isRealTool);
  if (pool.length === 0) return null;

  if (ctx.mode === RESPONSE_MODES.SHORT) {
    const survivors = runCascade(
      pool,
      [
        shortAnswerSuccessCriteriaGenreStage(ctx),
        ...standardStages(ctx),
        shortAnswerSuccessCriteriaMarksStage(ctx),
      ],
      trace
    );
    const pick = rankSurvivors(survivors, ctx)[0];
    return pick ? [pick] : null;
  }

  const survivors = rankSurvivors(
    runCascade(pool, standardStages(ctx), trace),
    ctx
  );
  if (survivors.length === 0) return null;

  // Keep the pick coherent: restrict to the set the best survivor belongs to.
  const bestKey = successCriteriaSetKey(survivors[0]);
  const set = survivors.filter((t) => successCriteriaSetKey(t) === bestKey);

  const sectionsIn = (tools) => {
    const sections = new Map();
    const unsectioned = [];
    tools.forEach((tool) => {
      const section = sectionOf(tool.title);
      if (!section) {
        unsectioned.push(tool);
        return;
      }
      if (!sections.has(section)) sections.set(section, []);
      sections.get(section).push(tool);
    });
    return { sections, unsectioned };
  };
  const { sections, unsectioned } = sectionsIn(set);
  const allSections = sectionsIn(survivors).sections;

  // Unsectioned set (NZ checklists, bare-genre AU tools): single best pick.
  if (sections.size === 0 && allSections.size === 0) return [survivors[0]];

  const wanted = neededSections(ctx);
  const picks = [];
  const pushSectionPicks = (section) => {
    // Prefer the coherent set; when it lacks a section (idiosyncratic
    // teacher titles, incomplete sets), fall back to any survivor that
    // provides it.
    const candidates =
      (sections.get(section) || []).length > 0
        ? sections.get(section)
        : allSections.get(section) || [];
    if (candidates.length === 0) return;
    const ranked = [...candidates].sort(
      (a, b) =>
        sectionVariantScore(b, ctx) - sectionVariantScore(a, ctx) ||
        String(a?.id || '').localeCompare(String(b?.id || ''))
    );
    // A section can have several DISTINCT parts (DBQ body: evidence from
    // documents / beyond / sourcing) as well as VARIANTS of one part
    // (single vs multiple, with vs without stimulus). Keep every distinct
    // part; collapse variants onto the best-ranked one.
    const seenBases = new Set();
    ranked.forEach((tool) => {
      const base = normalizeCompact(
        String(tool.title || '')
          .replace(
            /[-–—:]?\s*(stimulus\s*question|section\s+[ivx0-9]+)\s*$/i,
            ''
          )
          .replace(/\((single|multiple|full|single\s+paragraph)\)/gi, '')
          .replace(/\bparagraphs\b/gi, 'paragraph')
      );
      if (seenBases.has(base)) return;
      seenBases.add(base);
      picks.push(tool);
    });
  };

  wanted.forEach(pushSectionPicks);
  // Genre-set extras (Counterclaim, Complexity, Transitions...) belong with
  // a full response; for targeted paragraphs only the asked-for sections
  // apply. Only genuinely sectioned sets contribute extras — when the best
  // set is a standalone checklist and the sections came from a fallback
  // set, mixing the checklist in would pair unrelated genres.
  if (ctx.mode !== RESPONSE_MODES.TARGETED && sections.size > 0) {
    unsectioned.forEach((tool) => {
      if (!picks.includes(tool)) picks.push(tool);
    });
  }

  if (picks.length === 0) return [survivors[0]];
  return picks.slice(0, 8);
};

// ---------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------

/**
 * Compute the recommended tool (list) per feedback-type slot. Returns
 * { rubric, commentBank, successCriteria, shortAnswer, trace } where the
 * first two are single tools or null, successCriteria is a list or null,
 * shortAnswer is a single tool or null.
 */
export const matchTools = (
  {
    commentBanks = [],
    markingCriterias = [],
    successCriteria = [],
    shortAnswers = [],
  },
  rawContext = {}
) => {
  const ctx = deriveMatchContext(rawContext);
  const trace = {
    context: ctx,
    rubric: [],
    commentBank: [],
    successCriteria: [],
    shortAnswer: [],
  };
  return {
    rubric: selectRubric(markingCriterias, ctx, trace.rubric),
    commentBank: selectCommentBank(commentBanks, ctx, trace.commentBank),
    successCriteria: selectSuccessCriteria(
      successCriteria,
      ctx,
      trace.successCriteria
    ),
    shortAnswer: selectShortAnswer(shortAnswers, ctx, trace.shortAnswer),
    trace,
  };
};
